<script lang="ts">
  import {
    Chart,
    DoughnutController,
    ArcElement,
    Tooltip,
    type ChartConfiguration,
    type Plugin,
  } from 'chart.js'
  import { chartTheme, tooltipStyle } from '../../composables/chartTheme'
  import { tStore } from '../../i18n'

  // Register only the pieces this chart needs — keeps the Chart.js bundle
  // tree-shaken. Registration is idempotent, so repeating it per component is
  // safe.
  Chart.register(DoughnutController, ArcElement, Tooltip)

  interface Props {
    satisfied: number
    total: number
    label?: string
    size?: number
  }

  let { satisfied, total, label, size = 180 }: Props = $props()

  let canvas = $state<HTMLCanvasElement | undefined>()

  let percent = $derived(total > 0 ? Math.round((satisfied / total) * 100) : 0)

  // Draws the satisfied percentage in the hole of the doughnut.
  const centerTextPlugin: Plugin<'doughnut'> = {
    id: 'centerText',
    afterDraw(chart) {
      const { ctx, chartArea } = chart
      if (!chartArea) return
      const ds = chart.data.datasets[0]?.data as number[] | undefined
      const sat = ds?.[0] ?? 0
      const tot = (ds ?? []).reduce((a, b) => a + (b as number), 0)
      const pct = tot > 0 ? Math.round((sat / tot) * 100) : 0
      const x = (chartArea.left + chartArea.right) / 2
      const y = (chartArea.top + chartArea.bottom) / 2
      ctx.save()
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = chartTheme.text
      ctx.font = '700 26px Inter, system-ui, sans-serif'
      ctx.fillText(`${pct}%`, x, y)
      ctx.restore()
    },
  }

  function buildConfig(): ChartConfiguration<'doughnut'> {
    const unmet = Math.max(0, total - satisfied)
    const satLabel = $tStore('views.certificateAnalysis.legend.satisfied')
    const unmetLabel = $tStore('views.certificateAnalysis.legend.notSatisfied')
    return {
      type: 'doughnut',
      data: {
        labels: [satLabel, unmetLabel],
        datasets: [
          {
            data: [satisfied, unmet],
            backgroundColor: [chartTheme.satisfied, chartTheme.unmet],
            borderWidth: 0,
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '68%',
        animation: { duration: 500 },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed as number
                const pct = total > 0 ? Math.round((v / total) * 100) : 0
                return ` ${ctx.label}: ${v} · ${pct}%`
              },
            },
          },
        },
      },
      plugins: [centerTextPlugin],
    }
  }

  // Recreate the chart whenever the data changes; tear it down on unmount. The
  // dataset is small, so a full rebuild is cheaper than diffing.
  $effect(() => {
    if (!canvas) return
    void satisfied
    void total
    const chart = new Chart(canvas, buildConfig())
    return () => chart.destroy()
  })
</script>

<div
  class="donut"
  style="width: {size}px; height: {size}px"
  role="img"
  aria-label={label
    ? `${label}: ${percent}% ${$tStore('views.certificateAnalysis.satisfiedSuffix')}`
    : undefined}
>
  <canvas bind:this={canvas}></canvas>
</div>

<style>
  .donut {
    position: relative;
    max-width: 100%;
  }
</style>
